import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useUserOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { 
  BookOpen, 
  Search, 
  FileText, 
  HelpCircle, 
  Lightbulb,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  TrendingUp,
  Users,
  MessageCircle,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Bookmark,
  Share2,
  Download,
  Filter,
  Tag,
  Eye,
  Video
} from "lucide-react";

interface KBArticle {
  id: string;
  title: string;
  content: string;
  category: 'setup' | 'configuration' | 'troubleshooting' | 'security' | 'integration' | 'advanced';
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  lastUpdated: string;
  viewCount: number;
  helpfulCount: number;
  isFeatured?: boolean;
  isNew?: boolean;
}

interface KBCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  articleCount: number;
  color: string;
}

const kbArticles: KBArticle[] = [
  {
    id: '1',
    title: 'Complete DNS Filtering Setup Guide',
    content: `# Complete DNS Filtering Setup Guide

## Overview
This comprehensive guide will walk you through setting up DNS filtering for your organization from start to finish.

## Prerequisites
- Administrator access to your network
- Basic understanding of DNS concepts
- Access to your router or DNS server settings

## Step 1: Initial Configuration
1. Log into your DNS filtering dashboard
2. Create your first organization
3. Configure basic settings

## Step 2: Policy Creation
1. Navigate to Policies section
2. Click "Create New Policy"
3. Choose a template or start from scratch
4. Configure blocking rules

## Step 3: Network Integration
1. Update your DNS settings
2. Test the configuration
3. Monitor initial results

## Troubleshooting
Common issues and their solutions are covered in the troubleshooting section.`,
    category: 'setup',
    tags: ['setup', 'configuration', 'beginner', 'complete-guide'],
    difficulty: 'beginner',
    lastUpdated: '2024-01-20',
    viewCount: 1247,
    helpfulCount: 89,
    isFeatured: true
  },
  {
    id: '2',
    title: 'Advanced Policy Configuration Options',
    content: `# Advanced Policy Configuration Options

## Custom Rules
Learn how to create sophisticated filtering rules that go beyond basic category blocking.

## Time-based Policies
Configure policies that activate during specific hours or days.

## User Group Management
Set up different policies for different user groups within your organization.

## Integration APIs
Use our API to programmatically manage policies and integrate with existing systems.`,
    category: 'configuration',
    tags: ['advanced', 'policies', 'customization', 'api'],
    difficulty: 'advanced',
    lastUpdated: '2024-01-18',
    viewCount: 567,
    helpfulCount: 45,
    isNew: true
  },
  {
    id: '3',
    title: 'Troubleshooting Common DNS Issues',
    content: `# Troubleshooting Common DNS Issues

## Issue: Sites Not Loading
**Symptoms**: Websites fail to load or show error messages
**Causes**: DNS resolution problems, policy conflicts
**Solutions**: 
1. Check DNS server settings
2. Verify policy configuration
3. Clear DNS cache

## Issue: False Positives
**Symptoms**: Legitimate sites are blocked
**Causes**: Overly aggressive policies, outdated threat databases
**Solutions**:
1. Review policy settings
2. Add domains to whitelist
3. Submit false positive reports

## Issue: Performance Problems
**Symptoms**: Slow page loading, DNS timeouts
**Causes**: Network congestion, DNS server issues
**Solutions**:
1. Check network connectivity
2. Optimize DNS settings
3. Monitor performance metrics`,
    category: 'troubleshooting',
    tags: ['troubleshooting', 'problems', 'solutions', 'common-issues'],
    difficulty: 'intermediate',
    lastUpdated: '2024-01-15',
    viewCount: 892,
    helpfulCount: 67,
    isFeatured: true
  },
  {
    id: '4',
    title: 'Security Best Practices for DNS Protection',
    content: `# Security Best Practices for DNS Protection

## Policy Design Principles
1. **Defense in Depth**: Use multiple layers of protection
2. **Least Privilege**: Only allow necessary access
3. **Regular Updates**: Keep threat databases current

## Monitoring and Alerting
- Set up alerts for suspicious activity
- Monitor policy effectiveness
- Track threat trends

## Incident Response
- Document response procedures
- Train staff on security protocols
- Maintain incident logs

## Compliance Considerations
- Understand regulatory requirements
- Document security measures
- Regular security audits`,
    category: 'security',
    tags: ['security', 'best-practices', 'compliance', 'monitoring'],
    difficulty: 'intermediate',
    lastUpdated: '2024-01-12',
    viewCount: 734,
    helpfulCount: 52
  },
  {
    id: '5',
    title: 'Integrating with Enterprise Systems',
    content: `# Integrating with Enterprise Systems

## Active Directory Integration
Connect DNS filtering with your existing Active Directory infrastructure for seamless user management.

## SIEM Integration
Integrate with Security Information and Event Management systems for comprehensive security monitoring.

## Network Infrastructure
Learn how to integrate with various network devices and security appliances.

## API Documentation
Complete API reference for custom integrations and automation.`,
    category: 'integration',
    tags: ['integration', 'enterprise', 'active-directory', 'siem', 'api'],
    difficulty: 'advanced',
    lastUpdated: '2024-01-10',
    viewCount: 456,
    helpfulCount: 34
  }
];

const kbCategories: KBCategory[] = [
  {
    id: 'setup',
    name: 'Setup & Installation',
    description: 'Getting started guides and initial configuration',
    icon: <BookOpen className="h-6 w-6" />,
    articleCount: 1,
    color: 'bg-blue-500'
  },
  {
    id: 'configuration',
    name: 'Configuration',
    description: 'Policy setup and advanced configuration',
    icon: <FileText className="h-6 w-6" />,
    articleCount: 1,
    color: 'bg-green-500'
  },
  {
    id: 'troubleshooting',
    name: 'Troubleshooting',
    description: 'Problem solving and common issues',
    icon: <HelpCircle className="h-6 w-6" />,
    articleCount: 1,
    color: 'bg-yellow-500'
  },
  {
    id: 'security',
    name: 'Security',
    description: 'Security best practices and compliance',
    icon: <AlertCircle className="h-6 w-6" />,
    articleCount: 1,
    color: 'bg-red-500'
  },
  {
    id: 'integration',
    name: 'Integration',
    description: 'Third-party system integration',
    icon: <Lightbulb className="h-6 w-6" />,
    articleCount: 1,
    color: 'bg-purple-500'
  },
  {
    id: 'advanced',
    name: 'Advanced Topics',
    description: 'Expert-level features and techniques',
    icon: <Star className="h-6 w-6" />,
    articleCount: 1,
    color: 'bg-orange-500'
  }
];

const popularSearches = [
  'How to block social media',
  'DNS policy configuration',
  'Whitelist domains',
  'Threat detection setup',
  'Performance optimization',
  'User management',
  'API integration',
  'Security compliance'
];

export default function KnowledgeBase() {
  const { data: organization } = useUserOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Organization Required</h2>
          <p className="text-muted-foreground">
            Please set up your organization to access the knowledge base.
          </p>
        </div>
      </div>
    );
  }

  const filteredArticles = kbArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || article.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getCategoryColor = (categoryId: string) => {
    const category = kbCategories.find(c => c.id === categoryId);
    return category?.color || 'bg-gray-500';
  };

  const toggleArticle = (articleId: string) => {
    setExpandedArticle(expandedArticle === articleId ? null : articleId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SiteHeader />
      <SidebarInset>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Knowledge Base</h1>
              <p className="text-muted-foreground">
                Comprehensive documentation and guides for {organization.name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export KB
              </Button>
              <Button>
                <ExternalLink className="h-4 w-4 mr-2" />
                View Online
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search knowledge base articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 text-lg py-6"
            />
          </div>

          {/* Popular Searches */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground">Popular Searches</h3>
            <div className="flex flex-wrap gap-2">
              {popularSearches.map((search, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm(search)}
                  className="text-xs"
                >
                  {search}
                </Button>
              ))}
            </div>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="articles" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="articles">Articles</TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
              <TabsTrigger value="featured">Featured</TabsTrigger>
            </TabsList>

            {/* Articles Tab */}
            <TabsContent value="articles" className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex gap-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="all">All Categories</option>
                    {kbCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="all">All Levels</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                
                <Badge variant="outline">
                  {filteredArticles.length} articles found
                </Badge>
              </div>

              {/* Articles List */}
              <div className="space-y-4">
                {filteredArticles.map((article) => (
                  <Card key={article.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CardTitle className="text-lg">{article.title}</CardTitle>
                            {article.isFeatured && (
                              <Badge variant="default" className="bg-yellow-500">
                                <Star className="h-3 w-3 mr-1" />
                                Featured
                              </Badge>
                            )}
                            {article.isNew && (
                              <Badge variant="default" className="bg-green-500">
                                New
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Updated {formatDate(article.lastUpdated)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {article.viewCount} views
                            </span>
                            <span className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {article.helpfulCount} found helpful
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge variant="outline" className={getDifficultyColor(article.difficulty)}>
                            {article.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex flex-wrap gap-1">
                          {article.tags.map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Bookmark className="h-4 w-4 mr-2" />
                            Bookmark
                          </Button>
                          <Button size="sm" variant="outline">
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => toggleArticle(article.id)}
                          >
                            {expandedArticle === article.id ? 'Show Less' : 'Read More'}
                            {expandedArticle === article.id ? (
                              <ChevronDown className="h-4 w-4 ml-2" />
                            ) : (
                              <ChevronRight className="h-4 w-4 ml-2" />
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      {expandedArticle === article.id && (
                        <div className="mt-4 p-4 bg-muted rounded-lg">
                          <div className="prose prose-sm max-w-none">
                            <div dangerouslySetInnerHTML={{ 
                              __html: article.content.replace(/\n/g, '<br>').replace(/# (.*)/g, '<h2>$1</h2>').replace(/## (.*)/g, '<h3>$1</h3>')
                            }} />
                          </div>
                          <div className="flex gap-2 mt-4 pt-4 border-t">
                            <Button size="sm" variant="outline">
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Helpful
                            </Button>
                            <Button size="sm" variant="outline">
                              <MessageCircle className="h-4 w-4 mr-2" />
                              Comment
                            </Button>
                            <Button size="sm" variant="outline">
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Full Article
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {kbCategories.map((category) => (
                  <Card key={category.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-lg ${category.color} text-white`}>
                          {category.icon}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{category.name}</CardTitle>
                          <CardDescription>{category.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">
                          {category.articleCount} articles
                        </Badge>
                        <Button size="sm" variant="ghost">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Featured Tab */}
            <TabsContent value="featured" className="space-y-6">
              <div className="space-y-4">
                {kbArticles.filter(article => article.isFeatured).map((article) => (
                  <Card key={article.id} className="border-2 border-yellow-200 hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CardTitle className="text-lg">{article.title}</CardTitle>
                            <Badge variant="default" className="bg-yellow-500">
                              <Star className="h-3 w-3 mr-1" />
                              Featured
                            </Badge>
                          </div>
                          <CardDescription className="text-base">
                            {article.content.split('\n')[0]}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge variant="outline" className={getDifficultyColor(article.difficulty)}>
                            {article.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {article.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Bookmark className="h-4 w-4 mr-2" />
                            Bookmark
                          </Button>
                          <Button size="sm">
                            <BookOpen className="h-4 w-4 mr-2" />
                            Read Article
                            <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Additional Resources */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                Additional Resources
              </CardTitle>
              <CardDescription>
                More ways to learn and get help
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Button variant="outline" className="h-auto p-4 flex-col">
                  <Download className="h-8 w-8 mb-2" />
                  <span className="font-medium">PDF Manual</span>
                  <span className="text-xs text-muted-foreground">Download Guide</span>
                </Button>
                
                <Button variant="outline" className="h-auto p-4 flex-col">
                  <Video className="h-8 w-8 mb-2" />
                  <span className="font-medium">Video Library</span>
                  <span className="text-xs text-muted-foreground">Tutorial Videos</span>
                </Button>
                
                <Button variant="outline" className="h-auto p-4 flex-col">
                  <Users className="h-8 w-8 mb-2" />
                  <span className="font-medium">Community</span>
                  <span className="text-xs text-muted-foreground">User Forum</span>
                </Button>
                
                <Button variant="outline" className="h-auto p-4 flex-col">
                  <MessageCircle className="h-8 w-8 mb-2" />
                  <span className="font-medium">Support</span>
                  <span className="text-xs text-muted-foreground">Get Help</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
